import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WorkplanOrchestrator = ({ project }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  useEffect(() => {
    fetchTasks();
  }, [project.id]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/workplan-tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    return true;
  });

  const statusColors = {
    todo: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-blue-100 text-blue-800',
    blocked: 'bg-red-100 text-red-800',
    completed: 'bg-green-100 text-green-800'
  };

  const priorityColors = {
    low: 'border-gray-300',
    medium: 'border-yellow-300',
    high: 'border-orange-300',
    critical: 'border-red-400'
  };

  const tasksByPhase = {
    remediation: filteredTasks.filter((t) => t.task_type === 'remediation'),
    testing: filteredTasks.filter((t) => t.task_type === 'testing'),
    configuration: filteredTasks.filter((t) => t.task_type === 'configuration'),
    deployment: filteredTasks.filter((t) => t.task_type === 'deployment')
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Workplan & Orchestration</h2>
        <p className="text-gray-600 mb-6">Comprehensive migration workplan with task tracking</p>

        <div className="flex space-x-4 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="blocked">Blocked</option>
            <option value="completed">Completed</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {Object.entries(tasksByPhase).map(([phase, phaseTasks]) => (
            <div key={phase} className="border-2 border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize flex items-center">
                <span className="w-3 h-3 rounded-full bg-blue-600 mr-2"></span>
                {phase} ({phaseTasks.length})
              </h3>
              <div className="space-y-3">
                {phaseTasks.slice(0, 5).map((task) => (
                  <div
                    key={task.id}
                    className={`border-l-4 ${priorityColors[task.priority]} bg-white p-3 rounded shadow-sm hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[task.status]}`}>
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{task.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">{task.effort_hours}h effort</span>
                      <span className={`px-2 py-1 rounded ${
                        task.priority === 'critical' ? 'bg-red-50 text-red-700' :
                        task.priority === 'high' ? 'bg-orange-50 text-orange-700' :
                        'bg-gray-50 text-gray-600'
                      }`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                ))}
                {phaseTasks.length > 5 && (
                  <p className="text-sm text-gray-500 text-center py-2">
                    +{phaseTasks.length - 5} more tasks
                  </p>
                )}
                {phaseTasks.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No tasks in this phase</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
